import { useState, useCallback, useEffect } from 'react';

export interface AgentMetric {
  id: string;
  name: string;
  type: 'fast' | 'reasoning';
  status: 'running' | 'idle' | 'error' | 'stopped';
  lastHeartbeat: string;
  memoryUsage: number;
  uptime: string;
  tasksCompleted: number;
  cpuUsage: number;
  errorCount: number;
}

export interface SchedulerMetric {
  id: string;
  name: string;
  status: 'active' | 'paused' | 'error';
  queueSize: number;
  processedJobs: number;
  failedJobs: number;
  avgProcessingTime: number;
  lastActivity: string;
  throughput: number;
}

export interface BuildStackMetric {
  id: string;
  name: string;
  status: 'running' | 'completed' | 'failed' | 'queued';
  startTime: string;
  duration: number;
  steps: number;
  completedSteps: number;
  successRate: number;
  lastRun: string;
}

export interface CIMetric {
  id: string;
  pipeline: string;
  status: 'running' | 'passed' | 'failed' | 'cancelled';
  branch: string;
  commit: string;
  startTime: string;
  duration: number;
  testsPassed: number;
  testsFailed: number;
  coverage: number;
}

interface OperationsData {
  agents: AgentMetric[];
  scheduler: SchedulerMetric[];
  buildstacks: BuildStackMetric[];
  ci: CIMetric[];
  systemStatus: {
    isPaused: boolean;
    lastPauseTime?: string;
    pausedBy?: string;
  };
}

export const useOperationsMetrics = () => {
  const [data, setData] = useState<OperationsData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isControlLoading, setIsControlLoading] = useState(false);

  const fetchMetrics = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [agentsResponse, schedulerResponse, buildstacksResponse, ciResponse] = await Promise.all([
        fetch('/api/admin/metrics/agents'),
        fetch('/api/admin/metrics/scheduler'),
        fetch('/api/admin/metrics/buildstacks'),
        fetch('/api/admin/metrics/ci')
      ]);

      if (!agentsResponse.ok || !schedulerResponse.ok || !buildstacksResponse.ok || !ciResponse.ok) {
        throw new Error('Failed to fetch one or more metrics endpoints');
      }

      const [agents, scheduler, buildstacks, ci] = await Promise.all([
        agentsResponse.json(),
        schedulerResponse.json(),
        buildstacksResponse.json(),
        ciResponse.json()
      ]);

      setData({
        agents: agents || [],
        scheduler: scheduler || [],
        buildstacks: buildstacks || [],
        ci: ci || [],
        systemStatus: {
          isPaused: false,
          lastPauseTime: undefined,
          pausedBy: undefined
        }
      });
    } catch (err) {
      console.error('Failed to fetch operations metrics:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch operations metrics');
      
      setData({
        agents: [
          {
            id: 'agent_001',
            name: 'beehive-fast-01',
            type: 'fast',
            status: 'running',
            lastHeartbeat: new Date().toISOString(),
            memoryUsage: 512,
            uptime: '2h 34m',
            tasksCompleted: 45,
            cpuUsage: 23.5,
            errorCount: 2
          },
          {
            id: 'agent_002',
            name: 'beehive-reasoning-01',
            type: 'reasoning',
            status: 'running',
            lastHeartbeat: new Date().toISOString(),
            memoryUsage: 1024,
            uptime: '1h 12m',
            tasksCompleted: 12,
            cpuUsage: 45.2,
            errorCount: 0
          },
          {
            id: 'agent_003',
            name: 'beehive-fast-02',
            type: 'fast',
            status: 'idle',
            lastHeartbeat: new Date(Date.now() - 30000).toISOString(),
            memoryUsage: 256,
            uptime: '45m',
            tasksCompleted: 23,
            cpuUsage: 5.1,
            errorCount: 1
          }
        ],
        scheduler: [
          {
            id: 'scheduler_001',
            name: 'Main Task Scheduler',
            status: 'active',
            queueSize: 12,
            processedJobs: 1247,
            failedJobs: 23,
            avgProcessingTime: 2.4,
            lastActivity: new Date().toISOString(),
            throughput: 45.2
          },
          {
            id: 'scheduler_002',
            name: 'Background Processor',
            status: 'active',
            queueSize: 3,
            processedJobs: 892,
            failedJobs: 8,
            avgProcessingTime: 1.8,
            lastActivity: new Date(Date.now() - 60000).toISOString(),
            throughput: 32.1
          }
        ],
        buildstacks: [
          {
            id: 'build_001',
            name: 'Frontend Build',
            status: 'running',
            startTime: new Date(Date.now() - 300000).toISOString(),
            duration: 5,
            steps: 8,
            completedSteps: 6,
            successRate: 94.2,
            lastRun: new Date(Date.now() - 300000).toISOString()
          },
          {
            id: 'build_002',
            name: 'Backend API Build',
            status: 'completed',
            startTime: new Date(Date.now() - 600000).toISOString(),
            duration: 8,
            steps: 12,
            completedSteps: 12,
            successRate: 98.7,
            lastRun: new Date(Date.now() - 600000).toISOString()
          },
          {
            id: 'build_003',
            name: 'Database Migration',
            status: 'failed',
            startTime: new Date(Date.now() - 900000).toISOString(),
            duration: 3,
            steps: 5,
            completedSteps: 3,
            successRate: 87.3,
            lastRun: new Date(Date.now() - 900000).toISOString()
          }
        ],
        ci: [
          {
            id: 'ci_001',
            pipeline: 'main-ci',
            status: 'running',
            branch: 'main',
            commit: 'abc123f',
            startTime: new Date(Date.now() - 180000).toISOString(),
            duration: 3,
            testsPassed: 245,
            testsFailed: 2,
            coverage: 87.5
          },
          {
            id: 'ci_002',
            pipeline: 'feature-ci',
            status: 'passed',
            branch: 'feature/new-ui',
            commit: 'def456a',
            startTime: new Date(Date.now() - 480000).toISOString(),
            duration: 6,
            testsPassed: 198,
            testsFailed: 0,
            coverage: 92.1
          },
          {
            id: 'ci_003',
            pipeline: 'hotfix-ci',
            status: 'failed',
            branch: 'hotfix/security-patch',
            commit: 'ghi789b',
            startTime: new Date(Date.now() - 720000).toISOString(),
            duration: 4,
            testsPassed: 156,
            testsFailed: 12,
            coverage: 78.3
          }
        ],
        systemStatus: {
          isPaused: false,
          lastPauseTime: undefined,
          pausedBy: undefined
        }
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const pauseSystem = useCallback(async () => {
    setIsControlLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/admin/controls/pause', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to pause system');
      }

      await response.json();
      
      setData(prev => prev ? {
        ...prev,
        systemStatus: {
          isPaused: true,
          lastPauseTime: new Date().toISOString(),
          pausedBy: 'admin'
        }
      } : null);

      return { success: true, message: 'System paused successfully' };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to pause system';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setIsControlLoading(false);
    }
  }, []);

  const resumeSystem = useCallback(async () => {
    setIsControlLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/admin/controls/resume', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to resume system');
      }

      await response.json();
      
      setData(prev => prev ? {
        ...prev,
        systemStatus: {
          isPaused: false,
          lastPauseTime: undefined,
          pausedBy: undefined
        }
      } : null);

      return { success: true, message: 'System resumed successfully' };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to resume system';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setIsControlLoading(false);
    }
  }, []);

  const getAgentsByStatus = useCallback((status: AgentMetric['status']): AgentMetric[] => {
    if (!data) return [];
    return data.agents.filter(agent => agent.status === status);
  }, [data]);

  const getActiveBuilds = useCallback((): BuildStackMetric[] => {
    if (!data) return [];
    return data.buildstacks.filter(build => build.status === 'running');
  }, [data]);

  const getFailedCIPipelines = useCallback((): CIMetric[] => {
    if (!data) return [];
    return data.ci.filter(pipeline => pipeline.status === 'failed');
  }, [data]);

  useEffect(() => {
    fetchMetrics();
    const interval = setInterval(fetchMetrics, 30000);
    return () => clearInterval(interval);
  }, [fetchMetrics]);

  return {
    data,
    isLoading,
    error,
    isControlLoading,
    fetchMetrics,
    pauseSystem,
    resumeSystem,
    getAgentsByStatus,
    getActiveBuilds,
    getFailedCIPipelines
  };
};
